import { useState, useEffect, useRef } from 'react';
import { Heart, Send, Trash2, ChevronDown, Loader, MessageCircle } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (ts) => {
  if (!ts) return '';
  const diff = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff/60)}m`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const CommentItem = ({ comment, postId, onDelete, onLike, depth = 0 }) => {
  const [showReplies, setShowReplies] = useState(false);
  const [replies, setReplies] = useState([]);
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();

  const loadReplies = async () => {
    if (showReplies) { setShowReplies(false); return; }
    const res = await fetch(`${API_URL}/api/comments/${postId}/replies/${comment.comment_id}`, { credentials: 'include' });
    if (res.ok) { const d = await res.json(); setReplies(d.replies || []); }
    setShowReplies(true);
  };

  const submitReply = async () => {
    if (!replyText.trim() || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/comments/${postId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ text: replyText.trim(), parent_id: comment.comment_id }),
      });
      if (res.ok) {
        const newReply = await res.json();
        setReplies(prev => [...prev, { ...newReply, likes_count: 0, viewer_liked: false }]);
        setReplyText(''); setReplying(false);
        if (!showReplies) setShowReplies(true);
      }
    } finally { setSubmitting(false); }
  };

  return (
    <div className={`flex gap-2.5 ${depth > 0 ? 'ml-8 mt-2' : ''}`}>
      <Avatar className="w-7 h-7 flex-shrink-0 mt-0.5">
        <AvatarImage src={comment.user_avatar} />
        <AvatarFallback className="bg-gradient-to-br from-[#F4C430] to-[#FF6B35] text-white text-[10px] font-bold">
          {(comment.user_name || 'F')[0]}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        {/* Comment bubble */}
        <div className="bg-gray-50 rounded-2xl rounded-tl-sm px-3 py-2 inline-block max-w-full">
          <p className="text-xs font-semibold text-gray-900 mb-0.5">{comment.user_name}</p>
          <p className="text-sm text-gray-700 break-words">{comment.text}</p>
        </div>

        {/* Meta actions */}
        <div className="flex items-center gap-3 mt-1 px-1">
          <span className="text-[10px] text-gray-400">{formatDate(comment.created_at)}</span>
          <button
            onClick={() => onLike(comment.comment_id, comment.viewer_liked)}
            className={`text-xs font-medium transition-colors flex items-center gap-0.5 ${comment.viewer_liked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'}`}
          >
            <Heart className={`w-3 h-3 ${comment.viewer_liked ? 'fill-red-500' : ''}`} />
            {comment.likes_count > 0 && <span>{comment.likes_count}</span>}
          </button>
          {depth === 0 && (
            <button onClick={() => setReplying(v => !v)} className="text-xs font-medium text-gray-500 hover:text-[#FF6B35]">
              Reply
            </button>
          )}
          {comment.reply_count > 0 && depth === 0 && (
            <button onClick={loadReplies} className="text-xs text-[#FF6B35] font-medium flex items-center gap-0.5">
              <ChevronDown className={`w-3 h-3 transition-transform ${showReplies ? 'rotate-180' : ''}`} />
              {comment.reply_count} {comment.reply_count === 1 ? 'reply' : 'replies'}
            </button>
          )}
          {user?.user_id === comment.user_id && (
            <button onClick={() => onDelete(comment.comment_id)} className="text-[10px] text-gray-400 hover:text-red-500 ml-auto">
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Reply input */}
        {replying && (
          <div className="flex gap-2 mt-2 ml-1">
            <input value={replyText} onChange={e => setReplyText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && submitReply()}
              placeholder="Write a reply..."
              className="flex-1 text-xs border border-gray-200 rounded-2xl px-3 py-1.5 focus:outline-none focus:border-[#FF6B35]"
              autoFocus
            />
            <button onClick={submitReply} disabled={submitting || !replyText.trim()}
              className="text-[#FF6B35] disabled:opacity-40">
              <Send className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Replies */}
        {showReplies && replies.map(r => (
          <CommentItem key={r.comment_id} comment={r} postId={postId} onDelete={onDelete} onLike={onLike} depth={1} />
        ))}
      </div>
    </div>
  );
};

const CommentsThread = ({ postId, initialCount = 0, onCountChange }) => {
  const { user } = useAuth();
  const [comments, setComments]   = useState([]);
  const [total, setTotal]         = useState(initialCount);
  const [loading, setLoading]     = useState(false);
  const [open, setOpen]           = useState(false);
  const [text, setText]           = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const inputRef = useRef(null);

  const load = async (cursor = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '15' });
      if (cursor) params.set('cursor', cursor);
      const res = await fetch(`${API_URL}/api/comments/${postId}?${params}`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setComments(prev => cursor ? [...prev, ...d.comments] : d.comments);
        setTotal(d.total || 0);
        setNextCursor(d.next_cursor || null);
      }
    } finally { setLoading(false); }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && comments.length === 0) load();
    if (next) setTimeout(() => inputRef.current?.focus(), 100);
  };

  const submit = async () => {
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/comments/${postId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ text: text.trim() }),
      });
      if (res.ok) {
        const c = await res.json();
        setComments(prev => [{ ...c, likes_count: 0, reply_count: 0, viewer_liked: false }, ...prev]);
        setTotal(t => t + 1);
        onCountChange?.(total + 1);
        setText('');
      }
    } finally { setSubmitting(false); }
  };

  const handleLike = async (commentId, currentlyLiked) => {
    if (!user) return;
    setComments(prev => prev.map(c => c.comment_id === commentId
      ? { ...c, viewer_liked: !currentlyLiked, likes_count: c.likes_count + (currentlyLiked ? -1 : 1) }
      : c));
    await fetch(`${API_URL}/api/comments/${postId}/comments/${commentId}/like`, { method: 'POST', credentials: 'include' });
  };

  const handleDelete = async (commentId) => {
    await fetch(`${API_URL}/api/comments/${postId}/comments/${commentId}`, { method: 'DELETE', credentials: 'include' });
    setComments(prev => prev.filter(c => c.comment_id !== commentId));
    setTotal(t => Math.max(0, t - 1));
  };

  return (
    <div className="border-t border-gray-50 px-4 pb-3">
      {/* Toggle button */}
      <button onClick={toggle} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#FF6B35] py-2 transition-colors">
        <MessageCircle className="w-4 h-4" />
        <span className="font-medium">{total > 0 ? `${total} comment${total !== 1 ? 's' : ''}` : 'Comment'}</span>
        {total > 0 && <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />}
      </button>

      {open && (
        <div className="space-y-3">
          {/* Add comment input */}
          {user ? (
            <div className="flex gap-2 items-center">
              <Avatar className="w-7 h-7 flex-shrink-0">
                <AvatarImage src={user.picture} />
                <AvatarFallback className="bg-gradient-to-br from-[#F4C430] to-[#FF6B35] text-white text-[10px] font-bold">
                  {user.name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 flex items-center gap-2 bg-gray-50 rounded-2xl px-3 py-1.5">
                <input
                  ref={inputRef}
                  value={text}
                  onChange={e => setText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && submit()}
                  placeholder="Add a comment..."
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
                />
                <button onClick={submit} disabled={submitting || !text.trim()}
                  className="text-[#FF6B35] disabled:opacity-30 flex-shrink-0">
                  {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-400 text-center py-1">Sign in to comment</p>
          )}

          {/* Comments list */}
          {loading && comments.length === 0 && (
            <div className="flex justify-center py-4">
              <div className="w-5 h-5 border-2 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full animate-spin" />
            </div>
          )}
          {comments.length === 0 && !loading && (
            <p className="text-xs text-gray-400 text-center py-2">No comments yet — be first!</p>
          )}
          <div className="space-y-3">
            {comments.map(c => (
              <CommentItem key={c.comment_id} comment={c} postId={postId} onDelete={handleDelete} onLike={handleLike} />
            ))}
          </div>

          {/* Load more */}
          {nextCursor && (
            <button onClick={() => load(nextCursor)} disabled={loading}
              className="w-full text-xs text-[#FF6B35] py-1.5 hover:underline disabled:opacity-50">
              {loading ? 'Loading...' : 'Load more comments'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CommentsThread;
